import { Shield, Users, FileText, BarChart3, Video, Wrench, Building2, HardHat, Handshake, MessageCircle } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { PricingSection } from "../components/PricingSection";
import { CTABanner } from "../components/CTABanner";
import { MagneticButton } from "../components/MagneticButton";
import { Reveal } from "../components/Reveal";
import { WA_LINKS } from "../lib/utils";

const MANAGEMENT_FEATURES = [
{ icon: Users, title: "Tenant Sourcing & Verification", desc: "We find the right tenant , not just any tenant. Rigorous financial and background checks, professional reference verification, and legally airtight lease agreements." },
{ icon: BarChart3, title: "Rent Collection & Disbursement", desc: "Automated, reliable rent collection with timely disbursement to your account. Every transaction documented, every rupee accounted for." },
{ icon: Video, title: "Video Updates & On-Ground Inspection", desc: "Regular walkthrough videos from our field team. You see exactly what's happening , walls, fixtures, exteriors  from wherever you are in the world." },
{ icon: Wrench, title: "Maintenance Coordination", desc: "End to end maintenance management with trusted vetted vendors, before and after documentation, and transparent cost reporting." },
{ icon: FileText, title: "Legal & POA Support", desc: "Full Power of Attorney management, tenancy compliance, eviction support when needed, and ongoing legal documentation all handled professionally." },
{ icon: Shield, title: "Monthly Reports & Analytics", desc: "Clear, concise monthly performance summaries with rental yield analysis, expense tracking, and asset health scores. Know your property better than ever." }];


const ACQUISITION_FEATURES = [
{ icon: Building2, title: "Property Acquisition", desc: "Looking to invest in Indian real estate? We identify properties matching your criteria, conduct due diligence, negotiate on your behalf, and manage the entire transaction." },
{ icon: Handshake, title: "Asset Disposition (Sale)", desc: "When it's time to sell, we handle pricing strategy, marketing, buyer qualification, negotiation, and legal closures , maximizing your returns." },
{ icon: HardHat, title: "Construction & Renovation Partnerships", desc: "Full 360\xB0 support via our trusted construction company partners , from light refurbishment to full development ,boosting your property's value before listing." }];


export default function ServicesPage() {
  return (
    <>
      <section className="relative overflow-hidden bg-navy-800 pt-40 pb-28 noise-overlay" data-testid="services-hero">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=2000&q=80" alt="Luxury apartment interior" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        </div>
        <div className="container-luxury text-center">
          <Reveal><p className="eyebrow text-emerald-300 mb-5">Services</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-sans text-display-lg font-semibold text-white tracking-tight text-balance leading-tight mx-auto max-w-3xl">
              Complete Asset Management, <span className="font-display italic font-normal text-gold-300">End to End</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto text-balance leading-relaxed">From sourcing your ideal tenant to handling sale transactions  every service is designed for the NRI who demands quality, transparency, and results.</p>
          </Reveal>
        </div>
      </section>

      <section id="management" className="py-32 scroll-mt-24" data-testid="management-section">
        <div className="container-luxury">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-24 items-start">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <p className="eyebrow mb-4">Service 01</p>
                <h2 className="font-sans text-display-md font-semibold text-navy-800 tracking-tight leading-tight">
                  Comprehensive Property <span className="font-display italic font-normal text-emerald-500">Management</span>
                </h2>
                <p className="mt-6 text-lg text-navy-600/75 leading-relaxed">End-to-end management for luxury mansions, premium apartments, and commercial assets. We act as your trusted on-ground partner — protecting and growing your investment while you live your life.</p>
                <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-100 p-6">
                  <p className="text-sm font-semibold text-emerald-800 mb-2">Our Transparency Promise</p>
                  <p className="text-sm text-emerald-700/80 leading-relaxed">Regular video updates from on-ground inspections, detailed maintenance reports, monthly performance summaries, and comprehensive property analytics — giving you clear insights and full understanding of your asset's performance and health.</p>
                </div>
                <div className="mt-8">
                  <MagneticButton href={WA_LINKS.healthCheck} external size="lg" variant="primary" icon={<MessageCircle size={18} />}>Get a Free Property Health Check</MagneticButton>
                </div>
              </Reveal>
            </div>
            <div className="grid gap-6">
              {MANAGEMENT_FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal key={f.title} delay={i * 0.07}>
                    <div className="flex gap-4 p-6 rounded-2xl border border-navy-100 bg-white hover:shadow-luxury transition-shadow duration-300">
                      <div className="shrink-0 h-11 w-11 rounded-xl bg-navy-50 flex items-center justify-center text-navy-700"><Icon size={20} strokeWidth={1.5} /></div>
                      <div>
                        <h3 className="text-sm font-semibold text-navy-800 tracking-tight">{f.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-navy-600/65">{f.desc}</p>
                      </div>
                    </div>
                  </Reveal>);

              })}
            </div>
          </div>
        </div>
      </section>

      <div className="container-luxury"><div className="hairline" /></div>

      <section id="acquisition" className="py-32 scroll-mt-24" data-testid="acquisition-section">
        <div className="container-luxury">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-24 items-start">
            <Reveal y={40} className="order-last lg:order-first">
              <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-luxury-lg">
                <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=900&q=85" alt="Property acquisition consultation" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute bottom-6 left-6 right-6 rounded-xl bg-white/95 backdrop-blur-sm p-5 shadow-luxury">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-1">Brokerage Fee</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-navy-800">2%</span>
                    <span className="text-sm text-navy-500">of transaction value</span>
                  </div>
                  <p className="mt-1 text-xs text-navy-600/60">For both buying and selling · No hidden charges</p>
                </div>
              </div>
            </Reveal>
            <div>
              <Reveal>
                <p className="eyebrow mb-4">Service 02</p>
                <h2 className="font-sans text-display-md font-semibold text-navy-800 tracking-tight leading-tight">
                  Asset Acquisition <span className="font-display italic font-normal text-emerald-500">& Disposition</span>
                </h2>
                <p className="mt-6 text-lg text-navy-600/75 leading-relaxed">Whether you're investing in Indian real estate from abroad or looking to sell an existing asset  our experts guide you through every step, backed by 25+ years of market knowledge and trusted builder relationships.</p>
              </Reveal>
              <div className="mt-10 grid gap-6">
                {ACQUISITION_FEATURES.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <Reveal key={f.title} delay={0.1 + i * 0.1}>
                      <div className="flex gap-4">
                        <div className="shrink-0 h-11 w-11 rounded-xl bg-gold-50 flex items-center justify-center text-gold-600"><Icon size={20} strokeWidth={1.5} /></div>
                        <div>
                          <h3 className="text-sm font-semibold text-navy-800 tracking-tight">{f.title}</h3>
                          <p className="mt-1.5 text-sm leading-relaxed text-navy-600/65">{f.desc}</p>
                        </div>
                      </div>
                    </Reveal>);

                })}
              </div>
              <Reveal delay={0.4}>
                <div className="mt-10">
                  <MagneticButton href={WA_LINKS.call} external size="lg" variant="primary">Discuss Your Property Deal</MagneticButton>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <PricingSection />
      <CTABanner />
    </>);

}